<template>
  <div class="entropy-viz-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$S = -k_B Σ P_i ln(P_i)$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <svg width="100%" height="200" viewBox="0 0 400 200">
          <rect x="10" y="10" width="380" height="180" fill="none" stroke="#334155" stroke-width="2" />
          
          <!-- grid for entropy calculation visualization -->
          <g opacity="0.1" stroke="#fff" stroke-width="0.5">
             <line v-for="i in 18" :key="'v'+i" :x1="10 + i*20" y1="10" :x2="10 + i*20" y2="190" />
             <line v-for="i in 8" :key="'h'+i" x1="10" :y1="10 + i*20" x2="390" :y2="10 + i*20" />
          </g>

          <line v-if="state === 'ordered'" x1="200" y1="10" x2="200" y2="190" stroke="#ef4444" stroke-width="2" stroke-dasharray="4,4" />

          <circle v-for="p in particles" :key="p.id" :cx="p.x" :cy="p.y" r="3" :fill="p.color" />
        </svg>

        <div class="hud-panel">
           <div class="hud-item cyan">
              <label>系统实时信息熵 S</label>
              <span class="val">{{ currentEntropy.toFixed(3) }} <small>k_B</small></span>
           </div>
           <div class="hud-item orange">
              <label>理论最大熵极限</label>
              <span class="val">{{ maxEntropy.toFixed(3) }} <small>k_B</small></span>
           </div>
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="control-vertical-group">
              <div class="action-row">
                 <el-button type="success" size="small" plain @click="setOrdered" icon="el-icon-refresh-left">低熵初始态 (放回隔板)</el-button>
                 <el-button type="danger" size="small" plain @click="startMixing" :disabled="state === 'mixing'" icon="el-icon-video-play">移除隔板 (自发混合)</el-button>
              </div>
           </div>
        </div>
        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p><strong>真实物理计算：</strong>仿真区域被划分为真实网格。根据不同网格中粒子的分布概率 <code>P_i</code>，算法实时计算香农吉布斯熵方程 <code>S = -Σ P_i ln(P_i)</code>。移除隔板后，大量微观粒子由于碰撞随机游走，从<strong>低熵（高有序度集中）</strong>不可逆地膨胀至极大概率的均匀分布状态，直观揭示出热力学第二定律“孤立系统熵增”的微观统计本质规律。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'EntropyViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      count: 150,
      particles: [],
      state: 'ordered',
      timer: null,
      currentEntropy: 0,
      maxEntropy: Math.log(19 * 9) // number of valid grids 19x9 = 171
    }
  },
  methods: {
    initParticles() {
       this.particles = [];
       for (let i = 0; i < this.count; i++) {
          let color = i < this.count/2 ? '#ef4444' : '#0ea5e9';
          let x = i < this.count/2 ? Math.random() * 180 + 15 : Math.random() * 180 + 205;
          let y = Math.random() * 170 + 15;
          let angle = Math.random() * Math.PI * 2;
          let speed = 1.5 + Math.random() * 1.5;
          this.particles.push({
             id: i, color, x, y,
             vx: Math.cos(angle) * speed,
             vy: Math.sin(angle) * speed
          });
       }
    },
    setOrdered() {
       this.state = 'ordered';
       this.initParticles();
    },
    startMixing() {
       this.state = 'mixing';
    },
    computeEntropy() {
       let grid = {};
       for (let p of this.particles) {
          let gx = Math.floor((p.x - 10) / 20);
          let gy = Math.floor((p.y - 10) / 20);
          let key = `${gx},${gy}`;
          grid[key] = (grid[key] || 0) + 1;
       }
       let s = 0;
       for (let k in grid) {
          let p_i = grid[k] / this.count;
          if (p_i > 0) s -= p_i * Math.log(p_i);
       }
       if (this.currentEntropy === 0) this.currentEntropy = s;
       else this.currentEntropy = this.currentEntropy * 0.95 + s * 0.05; // Smooth numeric display
    },
    animate() {
       this.particles.forEach(p => {
          p.x += p.vx;
          p.y += p.vy;
          
          let boundLeft = 13;
          let boundRight = 387;
          let boundTop = 13;
          let boundBottom = 187;

          // Box boundaries
          if (p.x < boundLeft) { p.x = boundLeft; p.vx *= -1; }
          if (p.x > boundRight) { p.x = boundRight; p.vx *= -1; }
          if (p.y < boundTop) { p.y = boundTop; p.vy *= -1; }
          if (p.y > boundBottom) { p.y = boundBottom; p.vy *= -1; }

          // Separator collision
          if (this.state === 'ordered') {
             if (p.color === '#ef4444' && p.x > 197) { p.x = 197; p.vx *= -1; }
             if (p.color === '#0ea5e9' && p.x < 203) { p.x = 203; p.vx *= -1; }
          }
       });

       this.computeEntropy();
       this.timer = requestAnimationFrame(this.animate);
    }
  },
  mounted() {
    this.setOrdered();
    this.animate();
  },
  beforeDestroy() {
    if (this.timer) cancelAnimationFrame(this.timer);
  }
}
</script>

<style scoped lang="scss">
.entropy-viz-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0f9ff; color: #0891b2; border: 1px solid #cffafe; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { flex: 1; min-height: 200px; background: #0f172a; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; .hud-item { background: rgba(15, 23, 42, 0.82); border-radius: 8px; border: 1px solid #1e293b; padding: 6px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; margin-bottom: 2px; } .val { font-size: 14px; color: #f8fafc; font-weight: 800; font-family: monospace; small { font-size: 10px; color: #94a3b8; font-weight: normal; } } &.cyan { border-left: 3px solid #06b6d4; } &.orange { border-left: 3px solid #f59e0b; } } }
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-vertical-group { display: flex; flex-direction: column; gap: 8px; .action-row { display: flex; gap: 10px; .el-button { flex: 1; font-weight: 700; } } }
.discovery-card-compact { background: #f8fafc; padding: 10px 14px; border-radius: 10px; border: 1px solid #e2e8f0; display: flex; align-items: flex-start; gap: 10px; i { color: #2563eb; font-size: 1.1rem; margin-top: 2px; } p { flex: 1; margin: 0; font-size: 12px; color: #475569; line-height: 1.5; strong { color: #1e293b; } code { background: #e2e8f0; padding: 2px 4px; border-radius: 4px; font-family: monospace; } } }
</style>
